import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';
import { formatWooPrice, woocommerceApi } from '@/lib/api/services/woocommerce';
import { RefreshCw, ShoppingCart, Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useUrlParams } from '@/hooks/useUrlState';

interface WooCommerceAbandonedCartsProps {
    workspaceScopeId: string | null;
    isConnected: boolean;
    currency?: string;
}

type AbandonedOrder = {
    id: string;
    woocommerce_order_id: string;
    status?: string;
    currency?: string;
    total?: number | string;
    billing?: { first_name?: string; last_name?: string; email?: string } | null;
    line_items?: any;
    woocommerce_created_at?: string | null;
    created_at?: string;
};

const ensureItems = (items: any): { name?: string; quantity?: number }[] => {
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
        try { return JSON.parse(items) || []; } catch { return []; }
    }
    return [];
};

const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
};

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
];

const DEFAULT_LIMIT = 50;

export function WooCommerceAbandonedCarts({ workspaceScopeId, isConnected, currency = 'USD' }: WooCommerceAbandonedCartsProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { set: setUrl } = useUrlParams();
    const [orders, setOrders] = useState<AbandonedOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [syncing, setSyncing] = useState(false);

    const getParam = (key: string, def: string) => searchParams.get(key) || def;
    const setParam = (key: string, value: string) => {
        setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            p.set(key, value);
            return p;
        });
    };

    const page = parseInt(getParam('ac_page', '1'));
    const pageSize = parseInt(getParam('ac_page_size', '10'));

    const statusFilter = getParam('ac_status', 'all');
    const searchFromUrl = getParam('ac_search', '');
    const [searchInput, setSearchInput] = useState(searchFromUrl);

    useEffect(() => {
        setSearchInput(prev => (prev === searchFromUrl ? prev : searchFromUrl));
    }, [searchFromUrl]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== searchFromUrl) {
                setUrl({ ac_search: searchInput, ac_page: '1' }, { replace: true });
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput, searchFromUrl, setUrl]);

    const handleStatusChange = (value: string) => {
        setUrl({ ac_status: value === 'all' ? '' : value, ac_page: '1' }, { replace: true });
    };

    const loadOrders = async () => {
        if (!workspaceScopeId) return;
        setLoading(true);
        try {
            const res = await woocommerceApi.getAbandonedOrders(workspaceScopeId, DEFAULT_LIMIT, 0);
            setOrders(res.orders || []);
        } catch (error) {
            showApiError(error, { fallback: { title: 'Failed to load abandoned orders' } });
        } finally {
            setLoading(false);
            markLoaded();
        }
    };

    const handleSync = async () => {
        if (!workspaceScopeId) return;
        setSyncing(true);
        try {
            const results = await Promise.all([
                woocommerceApi.syncOrders(workspaceScopeId, 100, 'pending'),
                woocommerceApi.syncOrders(workspaceScopeId, 100, 'failed'),
                woocommerceApi.syncOrders(workspaceScopeId, 100, 'cancelled'),
            ]);
            await loadOrders();
            const total = results.reduce((sum, r: any) => sum + (r?.synced || 0), 0);
            toast(total > 0
                ? { title: 'Synced', description: `Fetched ${total} abandoned ${total === 1 ? 'order' : 'orders'} from WooCommerce.`, variant: 'success' }
                : { title: 'Up to date', description: 'No new abandoned orders to sync.', variant: 'info' }
            );
        } catch (err: any) {
            showApiError(err, { fallback: { title: 'Sync Failed' } });
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (isConnected && workspaceScopeId) loadOrders();
    }, [isConnected, workspaceScopeId]);

    const columns: Column<AbandonedOrder>[] = [
        {
            key: 'woocommerce_created_at',
            header: 'Date',
            render: (_, o) => {
                const date = o.woocommerce_created_at || o.created_at;
                if (!date) return <span className="text-muted-foreground">—</span>;
                return (
                    <div className="whitespace-nowrap">
                        <div>{format(new Date(date), 'MMM d, yyyy')}</div>
                        <div className="text-xs text-muted-foreground">{format(new Date(date), 'h:mm a')}</div>
                    </div>
                );
            },
        },
        {
            key: 'billing',
            header: 'Customer',
            render: (_, o) => {
                const b = o.billing;
                const name = (b?.first_name || b?.last_name)
                    ? `${b?.first_name || ''} ${b?.last_name || ''}`.trim()
                    : 'Guest';
                return (
                    <div>
                        <div className="font-medium text-sm">{name}</div>
                        {b?.email && <div className="text-xs text-muted-foreground">{b.email}</div>}
                    </div>
                );
            },
        },
        {
            key: 'line_items',
            header: 'Items',
            render: (_, o) => {
                const items = ensureItems(o.line_items);
                if (!items.length) return <span className="text-muted-foreground italic text-xs">No items</span>;
                return (
                    <div className="max-w-[200px] truncate text-sm" title={items.map(i => i.name).join(', ')}>
                        <span className="font-medium">{items.length} item{items.length > 1 ? 's' : ''}: </span>
                        {items[0]?.name}
                        {items.length > 1 && ` +${items.length - 1} more`}
                    </div>
                );
            },
        },
        {
            key: 'total',
            header: 'Total',
            render: (_, o) => (
                <span className="text-sm">{formatWooPrice(Number(o.total || 0), o.currency || currency)}</span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (_, o) => {
                const status = o.status || 'pending';
                return (
                    <span className={`capitalize text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {status}
                    </span>
                );
            },
        },
    ];

    const filtered = useMemo(() => {
        let result = orders;
        if (statusFilter !== 'all') {
            result = result.filter(o => (o.status || 'pending') === statusFilter);
        }
        const q = searchFromUrl.trim().toLowerCase();
        if (q) {
            result = result.filter(o => {
                const b = o.billing;
                const name = `${b?.first_name || ''} ${b?.last_name || ''}`.trim().toLowerCase();
                return (
                    name.includes(q) ||
                    (b?.email || '').toLowerCase().includes(q) ||
                    String(o.woocommerce_order_id || '').toLowerCase().includes(q)
                );
            });
        }
        return result;
    }, [orders, statusFilter, searchFromUrl]);

    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <ShoppingCart className="h-5 w-5" />
                            Abandoned Orders
                        </CardTitle>
                        <CardDescription>
                            Pending, failed, and cancelled orders from your WooCommerce store.
                        </CardDescription>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || loading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                            {syncing ? 'Syncing...' : 'Sync Orders'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={loadOrders} disabled={loading || syncing}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-4">
                    <div className="relative flex-1 sm:max-w-[300px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search customer, email or order #..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-9 h-9"
                        />
                        {searchInput && (
                            <X
                                className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer"
                                onClick={() => setSearchInput('')}
                            />
                        )}
                    </div>
                    <Select value={statusFilter} onValueChange={handleStatusChange}>
                        <SelectTrigger className="h-9 w-full sm:w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <DataTable
                    columns={columns}
                    data={paged}
                    loading={showListSkeleton({ loading: loading || syncing, hasLoaded, willFetch: isConnected && !!workspaceScopeId })}
                    currentPage={page}
                    pageSize={pageSize}
                    totalItems={filtered.length}
                    onPageChange={(p) => setParam('ac_page', String(p))}
                    onPageSizeChange={(s) => { setParam('ac_page_size', String(s)); setParam('ac_page', '1'); }}
                    pageSizeOptions={[10, 25, 50]}
                    emptyMessage={
                        <div className="text-center py-12 text-muted-foreground">
                            <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-20" />
                            <p className="text-sm">No abandoned orders found.</p>
                            <p className="text-xs mt-1">Click "Sync Orders" to fetch the latest data from your store.</p>
                        </div>
                    }
                />
            </CardContent>
        </Card>
    );
}
